import { isSafariBrowser } from "./constants";
import { VideoService } from "./services/video.service";
import { Category, ThumbnailSize, Thumbnailed, VideoDetails } from "./types";
import { enqueueSnackbar } from "notistack";

export function extensionFromContentDisposition(header?: string | null): string | null {
  if (!header) return null;
  let filename: string | null = null;
  const utf8Match = header.match(/filename\*\s*=\s*[^']*'[^']*'([^;]+)/i);
  if (utf8Match) {
    try { filename = decodeURIComponent(utf8Match[1].trim()); } catch { /* fall through */ }
  }
  if (!filename) {
    const quoted = header.match(/filename\s*=\s*"([^"]+)"/i);
    if (quoted) filename = quoted[1];
  }
  if (!filename) {
    const bare = header.match(/filename\s*=\s*([^;]+)/i);
    if (bare) filename = bare[1].trim();
  }
  if (!filename) return null;
  const dot = filename.lastIndexOf(".");
  if (dot <= 0 || dot === filename.length - 1) return null;
  return filename.slice(dot + 1);
}

export function downloadFile(url: string, name: string): void {
  // Create a link and set the URL using `createObjectURL`
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = url;
  link.download = name;

  // It needs to be added to the DOM so it can be clicked
  document.body.appendChild(link);
  link.click();

  // To make this work on Firefox we need to wait
  // a little while before removing it.
  setTimeout(() => {
    if (link && link.parentNode) {
      URL.revokeObjectURL(link.href);
      link.parentNode.removeChild(link);
    }
  }, 0);
}

export function padStartTime(n: number): string {
  return n.toString().padStart(2, "0");
}

export function intToHMS(secondsFloat: number): {
  hours: number;
  minutes: number;
  seconds: number;
} {
  const hours = Math.floor(secondsFloat / (60 * 60));
  const minutes = Math.floor((secondsFloat - hours * (60 * 60)) / 60);
  const seconds = Math.round(secondsFloat % 60);
  return { hours, minutes, seconds };
}

export function secondsToDurationString(secondsFloat: number) {
  const { hours, minutes, seconds } = intToHMS(secondsFloat);
  const parts: string[] = [];
  if (hours) parts.push(padStartTime(hours));
  parts.push(padStartTime(minutes));
  parts.push(padStartTime(seconds));
  return parts.join(":");
}

export function dateRepresent(dateAsString: string) {
  return {
    date: new Date(dateAsString).toLocaleDateString("en-CA"),
    time: new Date(dateAsString).toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

export const capitalizeFirstLetter = (letter: string) => {
  return String(letter[0].toUpperCase() + String(letter).slice(1));
};

export const handleNewsLetterList = (
  newsLetterService: () => Promise<any>,
  newsLetterData: { getAll: () => void },
  enqueueSnackbar: (message: string, options?: { variant: string }) => void,
  setLoadingState?: (loading: boolean) => void
) => {
  setLoadingState?.(true);
  newsLetterService()
    .then((response) => {
      enqueueSnackbar(response.data.message, { variant: "success" });
      newsLetterData.getAll();
    })
    .catch((err) => {
      enqueueSnackbar(err.response.data.message, { variant: "error" });
    })
    .finally(() => setLoadingState?.(false));
};

export function findCategoryPath(categoryId: number, allCategories: Category[]): Category[] {
  const path: Category[] = [];
  const traverse = (nodes: Category[], current: Category[]): boolean => {
    for (const node of nodes) {
      const next = [...current, node];
      if (node.id === categoryId) { path.push(...next); return true; }
      if (node.children && traverse(node.children, next)) return true;
    }
    return false;
  };
  traverse(allCategories, []);
  return path;
}

export function transformVideoTitle(video: VideoDetails): string {
  let videoTitle = video.title.trimStart().replaceAll(" ", "_") + ".";
  if (isSafariBrowser) {
    return videoTitle + "mp4";
  }
  return videoTitle;
}


export function thumbnailUrl(
  source: Thumbnailed | null | undefined,
  size: ThumbnailSize,
): string | undefined {
  if (!source) return undefined;
  return source.thumbnails?.[size] ?? source.url ?? source.preview ?? undefined;
}

export function is3dModel(type?: string | null): boolean {
  return type === "GLB" || type === "GLTF";
}

export function modelUrl(
  source: Thumbnailed | null | undefined,
): string | undefined {
  if (!source) return undefined;
  return source.thumbnails?.original ?? source.url ?? undefined;
}